<template>
    <div>
        <h3 class="my-3 text-lg font-medium leading-6 text-gray-400">
            Faturamento por Conta
        </h3>

        <form
            @submit.prevent="fetch(true)"
            class="flex content-center items-center"
        >
            <span class="w-[250px] max-w-[250px] flex-none">
                <date-range-picker
                    v-model="dateRange"
                    :formatter="{
                        date: 'DD/MM/YYYY',
                        month: 'MMM',
                    }"
                    :shortcuts="false"
                />
            </span>
            <div class="ml-2 grow">
                <input
                    v-model="filters.text"
                    type="text"
                    class="text-input"
                    placeholder="Filtre por nome ou e-mail"
                />
            </div>
            <div class="ml-2 flex flex-none items-center gap-2">
                <button
                    @click.prevent="clearFilters"
                    :disabled="loading"
                    type="button"
                    class="btn-outline-primary px-4 py-1"
                >
                    Limpar filtros
                </button>
                <submit-button
                    :loading="loading"
                    @click.prevent="fetch(true)"
                    size="sm"
                >
                    Filtrar
                </submit-button>
                <button
                    @click.prevent="exportCsv"
                    :disabled="exporting || loading"
                    type="button"
                    class="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {{ exporting ? 'Exportando...' : 'Exportar CSV' }}
                </button>
            </div>
        </form>

        <div class="mt-4 overflow-auto rounded-xl bg-slate-50 dark:bg-slate-800/25" style="max-height: calc(100vh - 280px)">
                    <table class="min-w-full table-auto border-collapse text-sm">
                        <thead class="sticky top-0 z-10 bg-slate-50 dark:bg-slate-700">
                            <tr class="text-slate-700 dark:text-white">
                                <th class="px-6 py-4 text-left text-sm font-medium">
                                    Nome
                                </th>
                                <th class="px-6 py-4 text-left text-sm font-medium">
                                    E-mail
                                </th>
                                <th
                                    v-for="month in months"
                                    :key="month"
                                    class="px-6 py-4 text-right text-sm font-medium"
                                >
                                    {{ formatMonthHeader(month) }}
                                </th>
                                <th class="px-6 py-4 text-right text-sm font-medium">
                                    Total
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-slate-800">
                            <tr v-if="loading">
                                <td
                                    :colspan="3 + months.length"
                                    class="p-8 text-center text-slate-400"
                                >
                                    Carregando...
                                </td>
                            </tr>
                            <tr v-else-if="rows.length === 0">
                                <td
                                    :colspan="3 + months.length"
                                    class="p-8 text-center text-slate-400"
                                >
                                    Nenhum resultado encontrado.
                                </td>
                            </tr>
                            <tr
                                v-for="row in rows"
                                :key="row.user_id"
                            >
                                <td class="border-b border-slate-100 p-4 pl-8 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                                    {{ row.name }}
                                </td>
                                <td class="border-b border-slate-100 p-4 pl-8 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                                    {{ row.email }}
                                </td>
                                <td
                                    v-for="month in months"
                                    :key="month"
                                    class="border-b border-slate-100 p-4 text-right text-slate-500 dark:border-slate-700 dark:text-slate-200"
                                >
                                    {{ formatCurrency(row.months[month] || 0) }}
                                </td>
                                <td class="border-b border-slate-100 p-4 text-right font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-100">
                                    {{ formatCurrency(row.total) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="my-5 flex w-full" v-if="pagination.total > 0">
                        <div class="mx-auto flex flex-col items-center">
                            <span class="mb-1 text-white">
                                Exibindo {{ pagination.from }} a
                                {{ pagination.to }} registros de
                                {{ pagination.total }}
                            </span>
                            <pagination
                                :pagination="pagination"
                                :offset="1"
                                @paginate="fetchPage"
                            ></pagination>
                        </div>
                    </div>
        </div>
    </div>
</template>

<script>
import Pagination from "../../../components/layouts/Pagination.vue";

export default {
    components: { Pagination },
    data() {
        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const defaultDateRange = [
            this.toDateString(startOfYear),
            this.toDateString(now),
        ];

        return {
            loading: false,
            exporting: false,
            dateRange: defaultDateRange,
            lastFetchedDateRange: [...defaultDateRange],
            filters: {
                text: '',
            },
            rows: [],
            months: [],
            pagination: {
                total: 0,
                per_page: 25,
                from: 0,
                to: 0,
                current_page: 1,
                last_page: 1,
            },
        };
    },
    watch: {
        dateRange: {
            deep: true,
            handler(newValue) {
                const normalized = this.normalizeDateRange(newValue);
                if (!this.hasValidDateRange(normalized)) {
                    return;
                }

                if (this.isSameDateRange(normalized, this.lastFetchedDateRange)) {
                    return;
                }

                this.fetch(true);
            },
        },
    },
    mounted() {
        this.fetch(true);
    },
    methods: {
        toDateString(date) {
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            return `${d}/${m}/${date.getFullYear()}`;
        },
        normalizeDateRange(value = this.dateRange) {
            if (Array.isArray(value)) {
                return value;
            }

            if (
                value &&
                typeof value === 'object' &&
                Object.prototype.hasOwnProperty.call(value, 'startDate') &&
                Object.prototype.hasOwnProperty.call(value, 'endDate')
            ) {
                return [value.startDate, value.endDate];
            }

            if (typeof value === 'string') {
                if (value.includes(' - ')) {
                    return value.split(' - ').map((item) => item.trim());
                }

                return [value, value];
            }

            return [];
        },
        hasValidDateRange(range) {
            if (!Array.isArray(range) || range.length !== 2) {
                return false;
            }

            return Boolean(this.toApiDate(range[0]) && this.toApiDate(range[1]));
        },
        isSameDateRange(firstRange, secondRange) {
            const first = this.normalizeDateRange(firstRange);
            const second = this.normalizeDateRange(secondRange);

            return first[0] === second[0] && first[1] === second[1];
        },
        toApiDate(value) {
            if (value instanceof Date && !Number.isNaN(value.getTime())) {
                const d = String(value.getDate()).padStart(2, '0');
                const m = String(value.getMonth() + 1).padStart(2, '0');

                return `${value.getFullYear()}-${m}-${d}`;
            }

            if (!value || typeof value !== 'string') {
                return null;
            }

            const parts = value.split('/');
            if (parts.length === 3) {
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }

            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return value;
            }

            if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
                return value.slice(0, 10);
            }

            return null;
        },
        parseDateRange() {
            const range = this.normalizeDateRange();

            return {
                start_date: this.toApiDate(range[0]),
                end_date: this.toApiDate(range[1]),
            };
        },
        fetch(reset = false) {
            if (reset) {
                this.pagination.current_page = 1;
            }

            const dates = this.parseDateRange();
            if (!dates.start_date || !dates.end_date) {
                return;
            }

            this.loading = true;
            const params = {
                ...dates,
                text: this.filters.text || undefined,
                page: this.pagination.current_page,
                per_page: this.pagination.per_page,
            };
            axios
                .get('/api/financial/billing-report', { params })
                .then(({ data }) => {
                    const rows = Array.isArray(data.data) ? data.data : [];
                    const months = Array.isArray(data.months) ? data.months : [];
                    const total = Number(data.total ?? rows.length) || 0;
                    const perPage = Number(data.per_page ?? this.pagination.per_page) || this.pagination.per_page;
                    const currentPage = Number(data.current_page ?? this.pagination.current_page) || this.pagination.current_page;
                    const lastPage = Number(data.last_page ?? Math.max(Math.ceil(total / perPage), 1)) || 1;
                    const from = Number(data.from ?? (total > 0 ? (currentPage - 1) * perPage + 1 : 0));
                    const to = Number(data.to ?? Math.min(currentPage * perPage, total));

                    this.rows = rows;
                    this.months = months;
                    this.pagination = {
                        ...this.pagination,
                        total,
                        from,
                        to,
                        current_page: currentPage,
                        per_page: perPage,
                        last_page: lastPage,
                    };

                    this.lastFetchedDateRange = [...this.normalizeDateRange()];
                })
                .catch((error) => {
                    this.rows = [];
                    this.months = [];
                    this.pagination = {
                        ...this.pagination,
                        total: 0,
                        from: 0,
                        to: 0,
                        current_page: 1,
                        last_page: 1,
                    };

                    if (this.$toast) {
                        this.$toast.error(error?.response?.data?.message || 'Erro ao carregar faturamento.');
                    }

                    console.error('Erro ao buscar faturamento:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchPage(page) {
            this.pagination.current_page = page;
            this.fetch();
        },
        clearFilters() {
            this.filters.text = '';
            const now = new Date();
            const startOfYear = new Date(now.getFullYear(), 0, 1);
            const defaultDateRange = [
                this.toDateString(startOfYear),
                this.toDateString(now),
            ];

            const shouldFetchManually = this.isSameDateRange(this.dateRange, defaultDateRange);
            this.dateRange = defaultDateRange;

            if (shouldFetchManually) {
                this.fetch(true);
            }
        },
        exportCsv() {
            this.exporting = true;
            const dates = this.parseDateRange();
            if (!dates.start_date || !dates.end_date) {
                this.exporting = false;

                return;
            }

            const params = {
                ...dates,
                text: this.filters.text || undefined,
            };
            axios
                .get('/api/financial/billing-report/export', { params })
                .then(({ data }) => {
                    const csvContent = data.rows
                        .map((row) => row.map((cell) => `"${cell}"`).join(';'))
                        .join('\n');
                    const blob = new Blob(
                        ['\uFEFF' + csvContent],
                        { type: 'text/csv;charset=utf-8;' }
                    );
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = data.filename;
                    link.click();
                    URL.revokeObjectURL(url);
                    this.exporting = false;
                })
                .catch((error) => {
                    if (this.$toast) {
                        this.$toast.error(error?.response?.data?.message || 'Erro ao exportar faturamento.');
                    }

                    console.error('Erro ao exportar faturamento:', error);
                    this.exporting = false;
                });
        },
        formatMonthHeader(month) {
            const [year, m] = month.split('-');
            const months = [
                'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
                'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
            ];
            return `${months[parseInt(m) - 1]}/${year}`;
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }).format(value);
        },
    },
};
</script>
